import { useMemo } from 'react';
import { KNOCKOUT_GAME_MIN_WIDTH } from '../../../../../constants/generic';
import { NOT_STARTED } from '../../../../../constants/statusTypes';
import { INormalizedKnockoutGroup } from '../../../../../types';
import { EventDate, GameScore, GameTeam, SMPFlex, SMPLinkWrapper } from '../../../../index';

export interface IKnockoutGameProps {
  isNational?: boolean;
  group: INormalizedKnockoutGroup;
  handleEventClick?: (id: string | number) => void;
  groupName?: string;
  sport?: string;
}

export const KnockoutGame = ({ isNational, group, handleEventClick, groupName, sport }: IKnockoutGameProps): JSX.Element => {
  const upcomingMatchDate = useMemo(() => {
    const match = group.matches[0];

    if (!match || match.statusType !== NOT_STARTED || !match.kickOffTime) {
      return null;
    }

    return <EventDate startTime={match.kickOffTime} />;
  }, [group.matches]);

  return (
    <div style={{ marginBottom: '8px', display: 'grid' }}>
      <SMPLinkWrapper href={group.matchLink} target={group.urlTarget} onClick={() => handleEventClick && handleEventClick(group.id)}>
        <SMPFlex
          variant='livescoreMatch'
          css={{
            border: '1px solid $knockoutGameBorderColor',
            borderRadius: '$3',
            width: '100%',
            py: '$1',
            px: '$3',
            backgroundColor: '$knockoutGameBgColor',
            minWidth: KNOCKOUT_GAME_MIN_WIDTH + 'px',
          }}
          justify='spaceBetween'
        >
          <SMPFlex variant='column' align='start' css={{ pr: '$1', height: 'auto' }}>
            {group?.teams?.map((team, _index, teams) => {
              if (!team || !teams[0] || !teams[1]) {
                return null;
              }

              const latestMatch = group.matches[group.matches.length - 1];
              const isWinner = team.winner?.value === 'YES' ? true : team.winner?.value === 'NO' ? false : undefined;

              const winnerReason = latestMatch?.winner;

              return (
                <GameTeam
                  isWinner={isWinner}
                  team={team}
                  key={team.id}
                  imageVariant='mediumLivescoreEntityLogo'
                  teamName={team.name}
                  teamLogo={team.logo}
                  isNational={isNational}
                  winnerId={isWinner ? team.id.toString() : undefined}
                  aggregateWinnerId={winnerReason?.aggregate?.id}
                  winnerReason={winnerReason?.match?.reason?.name || winnerReason?.aggregate?.reason?.name}
                  scoreDetails={
                    latestMatch?.aggregateScore || latestMatch?.penaltyShootoutScore
                      ? {
                          homeTeam: { id: teams[0].id.toString(), name: teams[0].name },
                          awayTeam: { id: teams[1].id.toString(), name: teams[1].name },
                          // The following logic is because in the second match of the knockout stage, the home and away teams are reversed:
                          aggregateScore: {
                            home: latestMatch.aggregateScore?.away,
                            away: latestMatch.aggregateScore?.home,
                          },
                          penaltyShootoutScore:
                            group.matches.length === 1
                              ? latestMatch.penaltyShootoutScore
                              : // The following logic is because in the second match of the knockout stage, the home and away teams are reversed:
                                {
                                  home: latestMatch.penaltyShootoutScore?.away,
                                  away: latestMatch.penaltyShootoutScore?.home,
                                },
                        }
                      : undefined
                  }
                />
              );
            })}
          </SMPFlex>
          <SMPFlex>
            {group.matches[0]?.statusType === NOT_STARTED ? (
              upcomingMatchDate
            ) : (
              <>
                <GameScore
                  eventId={group.id}
                  handleEventClick={handleEventClick}
                  verticalAlign
                  homeTeamScore={group?.totalScoreHomeTeamFirstMatch}
                  awayTeamScore={group?.totalScoreAwayTeamFirstMatch}
                  penaltyShootoutScore={group.matches[0]?.penaltyShootoutScore}
                  homeTeamWeight={group.totalScoreHomeTeamFirstMatch > group.totalScoreAwayTeamFirstMatch ? '700' : '400'}
                  awayTeamWeight={group.totalScoreAwayTeamFirstMatch > group.totalScoreHomeTeamFirstMatch ? '700' : '400'}
                  groupName={groupName}
                  sport={sport}
                />
                {group.matches.length > 1 && (
                  <SMPFlex css={{ paddingLeft: '$3' }}>
                    <GameScore
                      eventId={group.id}
                      handleEventClick={handleEventClick}
                      verticalAlign
                      homeTeamScore={group?.totalScoreAwayTeamSecondMatch}
                      awayTeamScore={group?.totalScoreHomeTeamSecondMatch}
                      penaltyShootoutScore={{
                        home: group.matches[group.matches.length - 1]?.penaltyShootoutScore?.away,
                        away: group.matches[group.matches.length - 1]?.penaltyShootoutScore?.home,
                      }}
                      homeTeamWeight={group.totalScoreAwayTeamSecondMatch > group.totalScoreHomeTeamSecondMatch ? '700' : '400'}
                      awayTeamWeight={group.totalScoreHomeTeamSecondMatch > group.totalScoreAwayTeamSecondMatch ? '700' : '400'}
                      groupName={groupName}
                      sport={sport}
                    />
                  </SMPFlex>
                )}
              </>
            )}
          </SMPFlex>
        </SMPFlex>
      </SMPLinkWrapper>
    </div>
  );
};
